// utils/mediaContentProcessor.js

import { RegularMediaProcessor } from './processors/regularMediaProcessor.js';
import { CarouselMediaProcessor } from './processors/carouselMediaProcessor.js';
import { GridMediaProcessor } from './processors/gridMediaProcessor.js';

export const processContentForMedia = (htmlContent) => {
  if (!htmlContent) return htmlContent;
  
  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;
  
  // Regex patterns for different media types
  const imagePattern = /https?:\/\/[^\s]+\.(?:jpg|jpeg|png|gif|webp|svg|bmp|ico)(?:\?[^\s]*)?/gi;
  const videoPattern = /https?:\/\/[^\s]+\.(?:mp4|webm|ogg|mov|avi|wmv|flv|mkv)(?:\?[^\s]*)?/gi;
  const youtubePattern = /https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/gi;
  
  // Layout pattern to detect grid layouts
  const layoutPattern = /^layout=\[grid\]\s+col=\[(\d+)\]$/i;

  // Helper function to create media items array
  const createMediaItems = (imageUrls, videoUrls, youtubeUrls) => {
    const items = [];
    
    imageUrls.forEach(url => {
      items.push({ type: 'image', url, alt: 'Image' });
    });
    
    videoUrls.forEach(url => {
      items.push({ type: 'video', url });
    });
    
    youtubeUrls.forEach(url => {
      items.push({ type: 'youtube', url });
    });
    
    return items;
  };

  // Generate unique IDs for media groups
  let mediaGroupCounter = 0;
  
  // Process all lists to group consecutive media list items
  const lists = tempDiv.querySelectorAll('ul, ol');
  lists.forEach(list => {
    const listItems = Array.from(list.querySelectorAll('li'));
    let mediaGroups = [];
    let currentGroup = [];
    let isGridLayout = false;
    let gridColumns = 3; // default
    
    listItems.forEach((li, index) => {
      // Get only the direct text content of this specific list item
      let itemText = '';
      for (let i = 0; i < li.childNodes.length; i++) {
        const child = li.childNodes[i];
        if (child.nodeType === Node.TEXT_NODE) {
          itemText += child.textContent;
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          if (!['UL', 'OL', 'LI'].includes(child.tagName)) {
            itemText += child.textContent;
          }
        }
      }
      
      // Check if this is a layout definition
      const layoutMatch = itemText.trim().match(layoutPattern);
      if (layoutMatch) {
        // This is a layout definition
        isGridLayout = true;
        gridColumns = parseInt(layoutMatch[1], 10);
        console.log(`Detected grid layout with ${gridColumns} columns`);
        
        // Mark this item for removal (it's just a layout definition)
        li.setAttribute('data-layout-definition', 'true');
        return; // Skip to next item
      }
      
      // Check for media URLs in this specific list item's text only
      const imageMatches = itemText.match(imagePattern) || [];
      const videoMatches = itemText.match(videoPattern) || [];
      const youtubeMatches = itemText.match(youtubePattern) || [];
      
      const mediaUrls = [...imageMatches, ...videoMatches, ...youtubeMatches];
      const hasMedia = mediaUrls.length > 0;
      
      if (hasMedia) {
        // Extract alignment classes from the list item
        const alignmentClasses = [];
        if (li.classList.contains('ql-align-center')) alignmentClasses.push('ql-align-center');
        if (li.classList.contains('ql-align-right')) alignmentClasses.push('ql-align-right');
        if (li.classList.contains('ql-align-justify')) alignmentClasses.push('ql-align-justify');
        
        // Add to current group
        currentGroup.push({
          element: li,
          text: itemText,
          mediaUrls: mediaUrls,
          imageMatches: imageMatches,
          videoMatches: videoMatches,
          youtubeMatches: youtubeMatches,
          alignmentClasses: alignmentClasses,
          isGridLayout: isGridLayout,
          gridColumns: gridColumns
        });
      } else {
        // If we have a group, save it and start a new one
        if (currentGroup.length > 0) {
          mediaGroups.push(currentGroup);
          currentGroup = [];
        }
        // Reset layout for new group
        isGridLayout = false;
        gridColumns = 3;
      }
      
      // If this is the last item and we have a group, save it
      if (index === listItems.length - 1 && currentGroup.length > 0) {
        mediaGroups.push(currentGroup);
      }
    });
    
    // Remove layout definition items
    list.querySelectorAll('[data-layout-definition="true"]').forEach(item => {
      item.remove();
    });
    
    // Process each media group
    mediaGroups.forEach(group => {
      if (group.length === 0) return;
      
      const groupId = `media-group-${mediaGroupCounter++}`;
      const isGrid = group[0]?.isGridLayout || false;
      const columns = group[0]?.gridColumns || 3;
      
      // Collect all media items for this group
      const allImageUrls = [];
      const allVideoUrls = [];
      const allYoutubeUrls = [];
      
      group.forEach((item) => {
        allImageUrls.push(...item.imageMatches);
        allVideoUrls.push(...item.videoMatches);
        allYoutubeUrls.push(...item.youtubeMatches);
      });
      
      const mediaItems = createMediaItems(allImageUrls, allVideoUrls, allYoutubeUrls);
      
      // Store media items in window object for access
      if (typeof window !== 'undefined') {
        window[`mediaItems_${groupId}`] = mediaItems;
        console.log(`Stored media items for ${groupId}:`, mediaItems);
      }
      
      // Create media layout for the group
      let mediaHTML = '';
      let groupText = '';
      let mediaIndex = 0;
      
      group.forEach((item) => {
        // Extract text without URLs
        let textWithoutUrls = item.text;
        item.mediaUrls.forEach((url) => {
          textWithoutUrls = textWithoutUrls.replace(url, '').trim();
        });
        if (textWithoutUrls) {
          groupText += (groupText ? ' • ' : '') + textWithoutUrls;
        }
        
        // Add images to layout
        item.imageMatches.forEach((url) => {
          if (isGrid) {
            mediaHTML += GridMediaProcessor.generateGridMediaHTML(groupId, 'image', url, mediaIndex);
          } else {
            mediaHTML += CarouselMediaProcessor.generateCarouselImageHTML(url);
          }
          mediaIndex++;
        });
        
        // Add videos to layout
        item.videoMatches.forEach((url) => {
          if (isGrid) {
            mediaHTML += GridMediaProcessor.generateGridMediaHTML(groupId, 'video', url, mediaIndex);
          } else {
            mediaHTML += CarouselMediaProcessor.generateCarouselVideoHTML(url);
          }
          mediaIndex++;
        });
        
        // Add YouTube videos to layout
        item.youtubeMatches.forEach((match) => {
          const videoId = match.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/)?.[1];
          if (videoId) {
            if (isGrid) {
              mediaHTML += GridMediaProcessor.generateGridMediaHTML(groupId, 'youtube', match, mediaIndex, videoId);
            } else {
              mediaHTML += CarouselMediaProcessor.generateCarouselYouTubeHTML(videoId);
            }
            mediaIndex++;
          }
        });
      });
      
      // Replace the first item in the group with the layout
      const firstItem = group[0].element;
      firstItem.classList.add('has-media');
      
      // Get alignment classes from the group (use the first item's alignment)
      const groupAlignment = group[0].alignmentClasses || [];
      
      let finalHTML = '';
      
      if (isGrid) {
        // Grid layout
        finalHTML = GridMediaProcessor.processGridMedia(groupId, mediaHTML, groupText, groupAlignment, columns);
      } else {
        // Determine if carousel is needed (more than 3 items)
        const totalMediaCount = group.reduce((count, item) => 
          count + item.imageMatches.length + item.videoMatches.length + item.youtubeMatches.length, 0
        );
        
        const needsCarousel = totalMediaCount > 3;
        const carouselId = `carousel-${Math.random().toString(36).substr(2, 9)}`;
        
        if (needsCarousel) {
          finalHTML = CarouselMediaProcessor.generateCarouselHTML(carouselId, mediaHTML, groupAlignment.join(' '));
        } else {
          // Simple horizontal layout without carousel navigation
          const alignmentClassString = groupAlignment.join(' ');
          if (groupText) {
            finalHTML += `<div class="list-media-text ${alignmentClassString}">${groupText}</div>`;
          }
          finalHTML += `<div class="list-media-horizontal ${alignmentClassString}">${mediaHTML}</div>`;
        }
      }
      
      firstItem.innerHTML = finalHTML;
      
      // Remove the other items in the group
      for (let i = 1; i < group.length; i++) {
        group[i].element.remove();
      }
    });
  });
  
  // Process regular media (non-list content) using RegularMediaProcessor
  RegularMediaProcessor.processRegularMedia(tempDiv);
  
  // Initialize functions
  CarouselMediaProcessor.initializeCarouselFunctions();
  GridMediaProcessor.initializeGridFunctions();
  
  return tempDiv.innerHTML;
};

// Function to get all combined styles
export const getAllMediaStyles = () => {
  return `
    ${RegularMediaProcessor.getStyles()}
    ${CarouselMediaProcessor.getStyles()}
    ${GridMediaProcessor.getStyles()}
  `;
};

// Export individual processors for direct use if needed
export { RegularMediaProcessor, CarouselMediaProcessor, GridMediaProcessor };